import { useState } from 'react'
import {
  Avatar,
  Box,
  Button,
  Container,
  darken,
  lighten,
  ListItemIcon,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
  AppBar
} from '@mui/material'
import { KeyboardArrowDown as KeyboardArrowDownIcon, Logout as LogoutIcon } from '@mui/icons-material'

import { tabProps } from '../TabPanel'
import { authActions } from '../../redux'
import { constants } from '../../config'
import { SourceToggle } from './sourceToggle'
import { SocrNavDrawer } from '../SocrNavDrawer'
import { SocrBoardStatusBar } from '../SocrBoardStatusBar'

const { catNames } = constants

const googleTabs = [
  { label: 'Dollar / SP', value: catNames.SP_MONTHLY },
  { label: 'Billing', value: catNames.BILLING },
  { label: 'SP Tracking', value: catNames.TRACKING },
  { label: 'Tickets', value: catNames.TICKETS_MONTHLY }
]

const n8nTabs = [
  { label: 'AI Dollar / SP', value: catNames.AI_SP_MONTHLY },
  { label: 'AI Billing', value: catNames.AI_BILLING },
  { label: 'AI SP Tracking', value: catNames.AI_TRACKING },
  { label: 'AI SP/Ticket', value: catNames.AI_TICKET_SP }
]

const DashboardAppBar = ({ props: viewProps = {}, controls = {} }) => {
  const { authUser, activeTab, dataSource, enabledN8nTabs = {} } = viewProps
  const { dispatch, handleTabChange, handleSourceChange } = controls
  const theme = useTheme()
  const isCompactChrome = useMediaQuery(theme.breakpoints.down('sm'))
  const tabsOnSecondRow = useMediaQuery(theme.breakpoints.down('md'))
  const [userMenuAnchor, setUserMenuAnchor] = useState(null)
  const userMenuOpen = Boolean(userMenuAnchor)

  const isN8n = dataSource === 'n8n'
  const tabs = isN8n ? n8nTabs : googleTabs
  const accent = isN8n ? darken(theme.custom.keysys.blue, 0.12) : theme.palette.success.main
  const selectedColor = isN8n ? lighten(theme.custom.keysys.blue, 0.18) : theme.palette.success.light
  const hairline = alpha('#fff', 0.12)
  const controlSurface = alpha('#000', 0.28)
  const controlBorder = `1px solid ${alpha('#fff', 0.14)}`

  const email = authUser?.email || ''
  const rawName = authUser?.displayName?.trim()
  const displayName = rawName || email || 'Signed in'
  const showEmailSubline = Boolean(email && rawName)
  const initial = String(displayName).slice(0, 1).toUpperCase()

  const tabsValue = tabs.some((t) => t.value === activeTab) ? activeTab : false
  const isTabDisabled = (tabValue) => isN8n && enabledN8nTabs?.[tabValue] === false
  const sourceLabel = isN8n ? 'N8N' : 'Google'

  const chartTabs = (
    <Tabs
      value={tabsValue}
      onChange={handleTabChange}
      textColor='inherit'
      variant={tabsOnSecondRow ? 'scrollable' : 'fullWidth'}
      scrollButtons={!tabsOnSecondRow}
      allowScrollButtonsMobile={false}
      sx={{
        minHeight: 32,
        width: '100%',
        '& .MuiTabs-flexContainer': {
          gap: 0.15
        },
        '& .MuiTabs-scrollButtons': {
          display: 'none'
        },
        '& .MuiTab-root': {
          minHeight: 32,
          minWidth: 0,
          flex: 1,
          px: { xs: 0.75, sm: 1, md: 1.1 },
          py: 0.35,
          borderRadius: 0.75,
          transition: 'background-color 140ms ease, color 140ms ease',
          fontSize: { xs: '0.7rem', sm: '0.74rem', md: '0.78rem' },
          fontWeight: 800,
          letterSpacing: 0.1,
          opacity: 0.72,
          '&.Mui-disabled': {
            opacity: 0.28
          },
          '&:hover': {
            opacity: 0.95,
            backgroundColor: alpha(accent, 0.12)
          },
          '&.Mui-selected': {
            opacity: 1,
            backgroundColor: alpha(accent, 0.2),
            color: selectedColor
          }
        }
      }}
      slotProps={{
        indicator: {
          sx: {
            bgcolor: accent,
            height: 2,
            borderRadius: 1
          }
        }
      }}
    >
      {tabs.map((tab) => (
        <Tab
          key={tab.value}
          label={tab.label}
          value={tab.value}
          disabled={isTabDisabled(tab.value)}
          {...tabProps(tab.value)}
        />
      ))}
    </Tabs>
  )

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: theme.zIndex.appBar,
        width: '100%'
      }}
    >
      <AppBar
        position='static'
        elevation={0}
        sx={{
          borderRadius: 0,
          border: 'none',
          boxShadow: 'none',
          backgroundImage: 'none',
          borderBottom: `1px solid ${hairline}`
        }}
      >
        <Container
          disableGutters
          sx={{
            px: { xs: 0.75, sm: 1, md: 1.25 },
            mx: 0,
            width: '100%',
            maxWidth: '100% !important'
          }}
        >
          {/* Primary chrome: brand · source · account */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 0.6, sm: 0.75, md: 0.85 },
              py: { xs: 0.55, sm: 0.65 },
              minHeight: 44
            }}
          >
            <SocrNavDrawer />

            <Box
              sx={{
                width: '1px',
                alignSelf: 'stretch',
                my: 0.5,
                bgcolor: hairline,
                display: { xs: 'none', sm: 'block' },
                flexShrink: 0
              }}
            />

            <SourceToggle
              props={{ dataSource, showLabels: !isCompactChrome, compact: isCompactChrome }}
              controls={{ onChange: handleSourceChange }}
            />

            {!tabsOnSecondRow
              ? (
                <>
                  <Box
                    sx={{
                      width: '1px',
                      alignSelf: 'stretch',
                      my: 0.5,
                      bgcolor: hairline,
                      flexShrink: 0
                    }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0, maxWidth: '100%' }}>
                    {chartTabs}
                  </Box>
                </>
                )
              : (
                <Box sx={{ flex: 1, minWidth: 0 }} />
                )}

            <Button
              id='dashboard-user-menu-button'
              onClick={(e) => setUserMenuAnchor(e.currentTarget)}
              color='inherit'
              aria-haspopup='true'
              aria-expanded={userMenuOpen ? 'true' : 'false'}
              aria-controls={userMenuOpen ? 'dashboard-user-menu' : undefined}
              aria-label={isCompactChrome ? `Account menu for ${displayName}` : undefined}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: isCompactChrome ? 0.1 : 0.65,
                minWidth: 0,
                maxWidth: isCompactChrome ? 'none' : 220,
                py: 0.25,
                px: isCompactChrome ? 0.3 : 0.6,
                borderRadius: 1,
                border: controlBorder,
                backgroundColor: controlSurface,
                textTransform: 'none',
                flexShrink: 0,
                '&:hover': {
                  borderColor: alpha('#fff', 0.22),
                  backgroundColor: alpha('#000', 0.4)
                }
              }}
            >
              <Avatar
                src={isCompactChrome ? undefined : (authUser?.photoURL || '')}
                sx={{
                  width: 26,
                  height: 26,
                  borderRadius: 0.75,
                  bgcolor: alpha(theme.custom.keysys.blue, 0.28),
                  flexShrink: 0,
                  fontSize: '0.75rem',
                  fontWeight: 800
                }}
              >
                {initial}
              </Avatar>
              {!isCompactChrome
                ? (
                  <Box sx={{ textAlign: 'left', minWidth: 0, flex: 1 }}>
                    <Typography
                      variant='body2'
                      sx={{ fontWeight: 700, lineHeight: 1.1, display: 'block', fontSize: '0.76rem' }}
                      noWrap
                    >
                      {displayName}
                    </Typography>
                    {showEmailSubline
                      ? (
                        <Typography
                          variant='caption'
                          sx={{ color: alpha('#fff', 0.5), lineHeight: 1.05, display: 'block', fontSize: '0.64rem' }}
                          noWrap
                        >
                          {email}
                        </Typography>
                        )
                      : null}
                  </Box>
                  )
                : null}
              <KeyboardArrowDownIcon
                sx={{
                  fontSize: 16,
                  opacity: 0.8,
                  flexShrink: 0,
                  transform: userMenuOpen ? 'rotate(180deg)' : 'none',
                  transition: 'transform 0.2s ease'
                }}
              />
            </Button>

            <Menu
              id='dashboard-user-menu'
              anchorEl={userMenuAnchor}
              open={userMenuOpen}
              onClose={() => setUserMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              slotProps={{
                paper: {
                  elevation: 8,
                  sx: {
                    mt: 1,
                    minWidth: 200,
                    borderRadius: 1.5,
                    border: `1px solid ${theme.palette.divider}`,
                    backgroundColor: theme.palette.background.paper
                  }
                }
              }}
            >
              <MenuItem
                onClick={() => {
                  setUserMenuAnchor(null)
                  dispatch(authActions.signOutUser())
                }}
              >
                <ListItemIcon>
                  <LogoutIcon fontSize='small' />
                </ListItemIcon>
                Log out
              </MenuItem>
            </Menu>
          </Box>

          {/* Chart tabs on their own row below md */}
          {tabsOnSecondRow
            ? (
              <Box
                sx={{
                  borderTop: `1px solid ${hairline}`,
                  mx: { xs: -0.75, sm: -1 },
                  px: { xs: 0.5, sm: 0.75 },
                  pb: 0.35,
                  pt: 0.15,
                  backgroundColor: alpha('#000', 0.14)
                }}
              >
                {chartTabs}
              </Box>
              )
            : null}

          {/* Active SOCR board context */}
          <Box sx={{ mx: { xs: -0.75, sm: -1, md: -1.25 } }}>
            <SocrBoardStatusBar
              trailing={(
                <Typography
                  component='span'
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.68rem',
                    letterSpacing: 0.4,
                    textTransform: 'uppercase',
                    color: alpha(accent, 0.95)
                  }}
                >
                  {sourceLabel}
                </Typography>
              )}
            />
          </Box>
        </Container>
      </AppBar>
    </Box>
  )
}

export { DashboardAppBar }
